import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { showErrorToast } from "../../../lib/toast";
import type { OptionNameValuePair } from "../../../lib/interfaces/types";
import { AuditType, AuditorType, AuditRelationEntityType, AUDIT_TYPE_LABELS, CaseAudit } from "../../../api/audits";
import BaseDataTable, { IColumnConfig, IDataRow } from "../../../components/ui/baseDataTable";
import BaseDataTableFrame, { BaseDataTableEmptyState, BaseDataTableSaveFooter } from "../../../components/ui/BaseDataTableFrame";
import ConductedByCell from "./ConductedByCell";
import EditableExpandableTextCell from "../../recommendations/components/PolicyImprovementsTable/EditableExpandableTextCell";

import { getAccountProductInfo } from "../../creditAccounts/logic/slice";
import { getCaseAccounts } from "../../accounts/logic/slice";
import { isCreditAreaOfFraud, isOperationalAreaOfFraud, isCyberAreaOfFraud } from "../../caseinfo/logic/Slice";
import { upsertCaseAudits } from "../logic/thunks";
import { selectAllCaseAudits } from "../logic/slice";

export interface CaseAuditRow extends CaseAudit {
  isAdded?: boolean;
  isDirty?: boolean;
}

const AUDIT_TYPE_OPTIONS: OptionNameValuePair<AuditType>[] = Object.values(AuditType).map(
  (value) => ({ name: AUDIT_TYPE_LABELS[value], value }),
);

const toLocalRows = (audits: CaseAudit[]): CaseAuditRow[] =>
  audits.map((audit) => ({ ...audit, isAdded: false, isDirty: false }));

export const OtherAuditsTable: React.FC = () => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const isCredit = useAppSelector(isCreditAreaOfFraud);
  const isOperational = useAppSelector(isOperationalAreaOfFraud);
  const isCyber = useAppSelector(isCyberAreaOfFraud);

  const savedAudits = useAppSelector(selectAllCaseAudits);
  const creditAccounts = useAppSelector(getAccountProductInfo);
  const caseAccounts = useAppSelector(getCaseAccounts);

  const [localRows, setLocalRows] = useState<CaseAuditRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalRows(toLocalRows(savedAudits));
  }, [savedAudits]);

  const targetAccountOptions: OptionNameValuePair<string>[] = useMemo(() => {
    if (isCredit) {
      return (creditAccounts || []).map((acc) => ({
        name: acc.account_name ? `${acc.account_name} (${acc.ext_account_number})` : acc.ext_account_number,
        value: acc.loan_reference_id,
      }));
    }
    if (isOperational || isCyber) {
      return (caseAccounts || []).map((acc) => ({
        name: acc.account_name ? `${acc.account_name} (${acc.account_number})` : acc.account_number,
        value: acc.account_number,
      }));
    }
    return [];
  }, [isCredit, isOperational, isCyber, creditAccounts, caseAccounts]);

  const handleAddRow = () => {
    setLocalRows((prev) => [
      ...prev,
      {
        audit_id: uuidv4(),
        audit_type: "" as AuditType,
        auditor_type: "" as AuditorType,
        auditor_name: null,
        target_accounts: [],
        audit_submission_date: "",
        adverse_observations: "",
        isAdded: true,
        isDirty: true,
      },
    ]);
  };

  const handleDeleteRow = (index: number) => {
    setLocalRows((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleRowChange = (index: number, value: any, field: keyof CaseAuditRow) => {
    setLocalRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== index) return row;

        if (field === "audit_type" && row.audit_type !== value) {
          return {
            ...row,
            audit_type: value,
            auditor_type: "" as AuditorType,
            auditor_name: null,
            isDirty: true,
          };
        }

        return { ...row, [field]: value, isDirty: true };
      }),
    );
  };

  const handleTargetAccountsChange = (index: number, selectedIds: string[]) => {
    const entity_type = isCredit ? AuditRelationEntityType.CREDIT_ACCOUNT : AuditRelationEntityType.ACCOUNT;
    handleRowChange(index, selectedIds.map((entity_id) => ({ entity_type, entity_id })), "target_accounts");
  };

  const hasChanges = useMemo(
    () => localRows.some((r) => r.isDirty),
    [localRows]
  );

  const hasValidationErrors = localRows.some(
    (row) => !row.audit_type || !row.auditor_type || !row.audit_submission_date?.trim(),
  );

  const handleSave = async () => {
    if (!caseId) return;

    const dirtyRows = localRows.filter((row) => row.isDirty);
    if (dirtyRows.length === 0) return;

    try {
      setIsSaving(true);
      const cleaned = dirtyRows.map(({ isAdded, isDirty, ...audit }) => audit);

      await dispatch(upsertCaseAudits({ caseId, data: cleaned })).unwrap();
    } catch {
      showErrorToast("Failed to save audit details.");
    } finally {
      setIsSaving(false);
    }
  };

  const columnConfigs: IColumnConfig<CaseAuditRow>[] = useMemo(
    () => [
      {
        headerName: "Audit Type",
        field: "audit_type",
        type: "select",
        isRequired: true,
        headerStyle: { width: "100px", minWidth: "120px", maxWidth: "120px" },
        getValue: (row) => row.audit_type ?? "",
        isEditable: true,
        onChange: handleRowChange,
        options: AUDIT_TYPE_OPTIONS,
        selectCellProps: {
          placeholder: "",
          className: "p-0",
          labelClassName: "p-0 py-1",
          rootContainerClassName: "w-full",
          titleClassName: "text-[12px] leading-[16px] font-medium text-[#0A0A0A]",
        },
      },
      {
        headerName: "Conducted By",
        field: "auditor_type",
        isRequired: true,
        type: "custom",
        headerStyle: { width: "160px", minWidth: "180px", maxWidth: "200px" },
        getValue: (row) => ({
          auditor_type: row.auditor_type,
          auditor_name: row.auditor_name,
        }),
        render: (idx) => {
          const row = localRows[idx];
          return (
            <div className="w-full">
              <ConductedByCell
                auditType={row?.audit_type}
                auditorType={row?.auditor_type}
                auditorName={row?.auditor_name}
                onChange={(value) => {
                  if (value?.auditor_type !== undefined) {
                    handleRowChange(idx, value.auditor_type, "auditor_type");
                  }
                  if (value?.auditor_name !== undefined) {
                    handleRowChange(idx, value.auditor_name ?? null, "auditor_name");
                  }
                }}
              />
            </div>
          );
        },
      },
      {
        headerName: "Target Accounts",
        field: "target_accounts",
        type: "multiselect",
        getValue: (row) => (row.target_accounts ?? []).map((target) => target.entity_id),
        isEditable: true,
        headerStyle: { width: "260px", minWidth: "240px", maxWidth: "280px" },
        onChange: (idx, value) => handleTargetAccountsChange(idx, value as string[]),
        options: targetAccountOptions,
        multiselectCellProps: {
          name: "Target Accounts",
          placeholder: "Select",
          hideChevron: true,
          showTooltip: false,
          className: "p-0",
          rootContainerClassName: "w-full",
          renderCustomView: (selectedOptions: OptionNameValuePair<string>[]) => {
            if (!selectedOptions?.length) return null;
            return (
              <div className="flex w-full flex-col items-start gap-1">
                {selectedOptions.map((opt) => (
                  <span
                    key={opt.value}
                    className="block max-w-full truncate font-[Inter] text-[12px] font-normal leading-[16px] text-[#0A0A0A]"
                  >
                    {opt.name}
                  </span>
                ))}
              </div>
            );
          },
        },
      },
      {
        headerName: "Audit Submission Date",
        field: "audit_submission_date",
        isRequired: true,
        type: "date",
        headerStyle: { width: "160px", maxWidth: "160px" },
        getValue: (row) => row.audit_submission_date ?? "",
        isEditable: true,
        onChange: handleRowChange,
        dateCellProps: {
          className: "w-full",
          inputClassName: "text-[12px] leading-[16px] font-normal text-[#0A0A0A]",
        },
      },
      {
        headerName: "Adverse Observations",
        field: "adverse_observations",
        type: "custom",
        getValue: (row) => row.adverse_observations ?? "",
        render: (idx, value) => (
          <EditableExpandableTextCell
            value={value as string}
            maxWidth="200px"
            className="text-[12px] leading-[16px] font-normal"
            onChange={(val) => handleRowChange(idx, val, "adverse_observations")}
          />
        ),
      },
    ],
    [localRows, targetAccountOptions],
  );

  const dataRows: IDataRow<CaseAuditRow>[] = useMemo(
    () =>
      localRows.map((row) => ({
        data: row,
        isEditable: true,
        widgets: isSaving
          ? []
          : row.isAdded
          ? ["add", "delete"]
          : ["add"],
        onAdd: handleAddRow,
        onDelete: handleDeleteRow,
      })),
    [localRows, isSaving]
  );

  return (
    <div className="flex w-full flex-col">
      <BaseDataTableFrame title="">
        <BaseDataTable<CaseAuditRow>
          columnConfigs={columnConfigs}
          dataRows={dataRows}
          showDefaultTableBody={dataRows.length === 0}
          defaultTableBody={
            <BaseDataTableEmptyState
              title="No Audits Added"
              subtitle="Record Audits and Inspections, especially between the Date of Occurrence and the Date of Detection"
              actionLabel="Add Audit"
              onAdd={handleAddRow}
            />
          }
        />
      </BaseDataTableFrame>

      {hasChanges && (
        <BaseDataTableSaveFooter
          isSaving={isSaving}
          disabled={hasValidationErrors}
          onSave={handleSave}
          onDiscard={() => setLocalRows(toLocalRows(savedAudits))}
        />
      )}
    </div>
  );
};

export default OtherAuditsTable;